/*
 * /inventory — HepCURE Inventory Tracker
 *
 * Thin composer for the /inventory route. All heavy lifting lives in
 * ./inventory/* submodules:
 *
 *   • constants.js         — colors, API URL, InvCtx + useInv, fetch helpers
 *   • seed.js              — SEED_* defaults + tiny label helpers
 *   • ui.jsx               — SectionHeader / TeamBadge / StatusPill / SortHeader
 *   • useInventoryStore.js — single source of truth + CRUD actions
 *   • StaffCountBody.jsx   — Monthly count "Request from HQ" table + dedup modal
 *   • AdminRequests.jsx    — Pending queue with bulk-approve + slip PDFs
 *   • AdminReceiving.jsx   — HQ stock, reorder rows, PO drafts, receiving history
 *   • AdminTransferLog.jsx — HQ→team audit trail + manual transfer form
 *   • AdminSuppliers.jsx   — Suppliers & items CRUD + adjustments + danger zone
 *   • AdminSignInStrip.jsx — 2-step PIN + password admin login pill
 *
 * Aligns with /app/design_guidelines.json: navy + teal, sharp cards,
 * high-density tables, Work Sans / IBM Plex Sans.
 */

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isAuthed } from "@/lib/consult/authSession";
import { isInventoryUnlocked } from "@/lib/inventory/authRuntime";
import { InvCtx, MIST, NAVY } from "./inventory/constants";
import { useInventoryStore } from "./inventory/useInventoryStore";
import { StaffSurface } from "./inventory/StaffCountBody";
import { AdminRequests } from "./inventory/AdminRequests";
import { AdminReceiving } from "./inventory/AdminReceiving";
import { AdminTransferLog } from "./inventory/AdminTransferLog";
import { AdminSuppliers } from "./inventory/AdminSuppliers";
import { AdminSignInStrip } from "./inventory/AdminSignInStrip";

export default function InventoryPreview() {
  const nav = useNavigate();
  const store = useInventoryStore();
  // Gate the whole page just like /outreach — anyone hitting
  // /inventory/dashboard directly without a session gets bounced back to
  // the PIN + password gate. The `/inventory-preview` legacy alias stays
  // open for stakeholder demos.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/inventory-preview") return;
    // Require both a valid session token AND an inventory unlock in this
    // JS runtime. A hard refresh wipes the runtime flag, sending the
    // user back to the PIN screen even though the token persists.
    if (!isAuthed() || !isInventoryUnlocked()) nav("/inventory", { replace: true });
  }, [nav]);
  return (
    <InvCtx.Provider value={store}>
    <div className="min-h-screen pt-20" style={{ background: MIST }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        {/* Hero banner — restored per user request. Sign-in button lives on
            the right so admins can jump in without hunting for the strip. */}
        <div className="rounded-xl p-5 sm:p-6 shadow-sm text-white" style={{ background: NAVY }} data-testid="inventory-hero-banner">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">HepCURE Inventory Tracker</h1>
              <p className="mt-2 text-xs sm:text-sm leading-relaxed text-white/85">
                All supplies land at HQ. Teams request from HQ with one tap · admin approves with one tap · every movement logged.
                <br className="hidden sm:inline" />
                {" "}No more spreadsheets, no more chasing whether teams actually got their supplies.
              </p>
            </div>
            <div className="shrink-0" data-testid="admin-signin-strip">
              <AdminSignInStrip store={store} />
            </div>
          </div>
        </div>

        {/* Staff surface — tabs between Monthly count and Request from HQ */}
        <StaffSurface />

        {/* Pending requests visible to everyone — standard users see
            their team's own submitted orders (read-only), admins get
            approve/deny/bulk controls layered on top. */}
        <AdminRequests />

        {/* Admin-only surfaces: HQ warehouse stock, transfers, and the
            supplier / item CRUD stay gated behind the admin token. */}
        {store.hasToken && (
          <>
            <AdminReceiving />
            <AdminTransferLog />
            <AdminSuppliers />
          </>
        )}

        <p className="text-center text-xs text-slate-500 pt-4 pb-8">
          Live inventory data · Sign in as admin to<br className="sm:hidden" />
          {" "}approve/deny requests, log receipts and manage suppliers.
        </p>
      </div>
    </div>
    </InvCtx.Provider>
  );
}
